import { Box, Header, Link, Popover, SpaceBetween } from '@cloudscape-design/components';

import { PhoneNumber } from './PhoneNumber';
import { StarsIcon } from './StarsIcon';

function percentileRatio(value: number): number {
  return Math.abs(value) > 1 ? value / 100 : value;
}

export function scorecardGradeBorderColor(grade?: string | null): string {
  if (!grade) return 'border-gray-400';
  if (grade.includes('A')) return 'border-green-600';
  if (grade.includes('B')) return 'border-green-400';
  if (grade.includes('C')) return 'border-yellow-400';
  return 'border-red-500';
}

export type ScorecardGradeProps = {
  grade?: string | null;
  percentile?: number | null;
  compact?: boolean;
  borderColor?: string;
};

export function ScorecardGrade({
  grade,
  percentile,
  compact = false,
  borderColor,
}: ScorecardGradeProps) {
  const gradeBox = (
    <div
      className={`text-center whitespace-nowrap border-box ${compact ? 'border-2 px-2 py-1 min-w-12' : 'border-4 px-4 pb-4 pt-2 w-24'} ${borderColor ?? scorecardGradeBorderColor(grade)}`}
      aria-label={grade ? `Scorecard grade ${grade}` : 'Scorecard grade unavailable'}
    >
      <Box
        variant="span"
        textAlign="center"
        fontWeight="heavy"
        fontSize={compact ? 'heading-xl' : 'display-l'}
      >
        {grade ?? '—'}
      </Box>
    </div>
  );

  if (grade == null || percentile == null) return gradeBox;
  return (
    <Popover
      position="bottom"
      size="small"
      triggerType="text"
      header="Letter Grade"
      content={
        <Box variant="span" textAlign="center">
          Better than {(100 * percentileRatio(percentile)).toFixed(1)}% of similar businesses
        </Box>
      }
    >
      {gradeBox}
    </Popover>
  );
}

export type ScorecardIdentityProps = {
  name: string;
  address: string;
  grade?: string | null;
  percentile?: number | null;
  rating?: number | null;
  reviewCount?: number | null;
  reviewLink?: string | null;
  sourceName?: string;
  phoneNumber?: string | null;
  gradeBorderColor?: string;
};

export function ScorecardIdentity({
  name,
  address,
  grade,
  percentile,
  rating,
  reviewCount,
  reviewLink,
  sourceName = 'Google',
  phoneNumber,
  gradeBorderColor,
}: ScorecardIdentityProps) {
  const reviewText = `${reviewCount?.toLocaleString() ?? '—'} ${sourceName} ${reviewCount === 1 ? 'review' : 'reviews'}`;
  return (
    <SpaceBetween direction="horizontal" size="s">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
        <ScorecardGrade grade={grade} percentile={percentile} borderColor={gradeBorderColor} />
        <div>
          <SpaceBetween direction="vertical" size="s">
            <Header variant="h2" description={address}>
              {name}
            </Header>
            <SpaceBetween direction="horizontal" size="xs">
              <Box variant="span">{rating?.toFixed(1) ?? '—'}</Box>
              <StarsIcon stars={rating ?? undefined} />
              {reviewLink ? (
                <Link href={reviewLink} target="_blank">
                  {reviewText}
                </Link>
              ) : (
                <Box variant="span" color="text-body-secondary">
                  {reviewText}
                </Box>
              )}
            </SpaceBetween>
            {phoneNumber ? <PhoneNumber phoneNumber={phoneNumber} /> : null}
          </SpaceBetween>
        </div>
      </div>
    </SpaceBetween>
  );
}
